import { useCallback, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { pickExpiringVoucher } from 'lib/voucher/pickExpiringVoucher'
import { useActiveVouchersContext } from 'marketing/contexts/ActiveVouchersContext'
import {
  getSeenExpiringVoucherIds,
  hasShownExpiringVoucherReminderThisSession,
  markExpiringVoucherSeen,
} from 'marketing/storage/expiringVoucherReminder'

type Options = {
  enabled?: boolean
}

/**
 * Finds the logged-in customer's active voucher that expires first within the next 48 hours (GR-5151),
 * skipping vouchers this device has already been reminded about. A customer gets at most one
 * reminder per browser session.
 *
 * `isReady` stays false until auth has initialised and the active voucher list has settled, so a
 * caller can hold back other voucher prompts instead of letting one open and then be replaced by
 * this voucher.
 *
 * Seen vouchers and the session flag are read once per mount. `markSeen` locks in the voucher being
 * shown, so a refetched voucher list cannot swap it for another, and `dismiss` clears it for the rest
 * of this mount.
 */
function useExpiringVoucher({ enabled = true }: Options = {}) {
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const isAuthInitialised = useAppSelector(state => state.auth.initComplete)
  const isEnabled = enabled && !!memberId
  const { vouchers, isLoading } = useActiveVouchersContext({ enabled: isEnabled })
  const [seenVoucherIds] = useState(getSeenExpiringVoucherIds)
  const [hasRemindedThisSession] = useState(hasShownExpiringVoucherReminderThisSession)
  const [shownVoucher, setShownVoucher] = useState<App.PublicVoucher>()
  const [isDismissed, setIsDismissed] = useState(false)

  const candidateVoucher = useMemo(() => {
    if (!isEnabled || hasRemindedThisSession) return undefined
    const unseenVouchers = vouchers.filter(candidate => !seenVoucherIds.includes(candidate.voucherId))
    return pickExpiringVoucher(unseenVouchers, Date.now())
  }, [isEnabled, hasRemindedThisSession, vouchers, seenVoucherIds])

  const voucher = isDismissed ? undefined : shownVoucher ?? candidateVoucher

  const markSeen = useCallback(() => {
    // Only the first voucher shown is recorded, so a later call cannot burn another voucher's reminder.
    if (shownVoucher || isDismissed || !candidateVoucher) return
    setShownVoucher(candidateVoucher)
    markExpiringVoucherSeen(candidateVoucher.voucherId)
  }, [shownVoucher, isDismissed, candidateVoucher])

  const dismiss = useCallback(() => setIsDismissed(true), [])

  return {
    voucher,
    isReady: !enabled || (isAuthInitialised && (!isEnabled || !isLoading)),
    markSeen,
    dismiss,
  }
}

export default useExpiringVoucher
